import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useRevisarMaestro, type RevisionDelMaestro as Revision } from './api';

/**
 * RevisionDelMaestro — sincroniza a todo el mundo con la Base de Datos
 * Empleados y dice qué encontró.
 *
 * El endpoint existe desde el backfill inicial y la revisión del 2026-08-26 lo
 * convirtió además en el saneador del maestro. Nunca tuvo botón: se corría con
 * `curl`. Esta pantalla es su grifo, y mantiene la regla que traía escrita:
 * **revisar e inactivar son dos pulsaciones distintas**, porque lo primero es
 * idempotente y lo segundo toca datos personales.
 */
export function RevisionDelMaestro({ onCerrar }: { onCerrar: () => void }) {
  const revisar = useRevisarMaestro();
  const [resultado, setResultado] = useState<Revision | null>(null);
  const [limpiando, setLimpiando] = useState(false);

  // La revisión arranca al abrir: es de solo lectura mientras no se pida
  // inactivar nada.
  useEffect(() => {
    revisar.mutate(false, { onSuccess: setResultado });
    // Una sola vez, al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const huerfanas = resultado?.huerfanas?.total ?? 0;
  const limpiadas = resultado?.huerfanas?.limpiadas?.length ?? 0;
  const sueltos = Object.entries(resultado?.expediente_sin_dueno ?? {});

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v && !revisar.isPending) onCerrar(); }}
      titulo="Revisión del maestro"
      descripcion="Sincroniza a cada colaborador con su ficha y revisa la integridad de la Base de Datos Empleados."
      ancho="max-w-xl"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar} disabled={revisar.isPending}>Cerrar</Button>
          {huerfanas > 0 && !limpiadas && (
            <Button
              variant="destructive"
              loading={revisar.isPending && limpiando}
              onClick={() => {
                setLimpiando(true);
                revisar.mutate(true, {
                  onSuccess: (r) => {
                    setResultado(r);
                    Utils.toast('Fichas sin dueño inactivadas', 'success');
                  },
                  onError: (err) => Utils.toast(
                    err instanceof Error ? err.message : 'No se pudo sanear', 'error'),
                });
              }}
            >
              <i className="bi bi-eraser" aria-hidden="true" />
              Inactivar {huerfanas === 1 ? 'la ficha sin dueño' : `las ${huerfanas} sin dueño`}
            </Button>
          )}
        </>
      }
    >
      {revisar.isPending && !resultado ? (
        <div className="flex flex-col gap-2 pb-2" aria-busy="true">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-5 w-56" />
        </div>
      ) : revisar.isError && !resultado ? (
        <EstadoError
          err={revisar.error}
          que="la revisión del maestro"
          onReintentar={() => revisar.mutate(false, { onSuccess: setResultado })}
        />
      ) : !resultado ? null : (
        <ul className="flex flex-col gap-1.5 pb-2 text-3">
          <Linea
            alerta={resultado.errores > 0}
            texto={
              <>
                <strong>{resultado.synced}</strong> colaboradores sincronizados
                {resultado.errores ? <> · <strong>{resultado.errores}</strong> con error</> : null}
              </>
            }
          />
          <Linea
            alerta={resultado.duplicados_consolidados > 0}
            texto={resultado.duplicados_consolidados
              ? <><strong>{resultado.duplicados_consolidados}</strong> ficha(s) duplicada(s)
                consolidada(s) — se conservó la original</>
              : 'Sin fichas duplicadas'}
          />
          <Linea
            alerta={huerfanas > 0 && !limpiadas}
            texto={limpiadas
              ? <><strong>{limpiadas}</strong> ficha(s) huérfana(s) inactivada(s), con auditoría</>
              : huerfanas
                ? <><strong>{huerfanas}</strong> ficha(s) sin dueño: guardan datos personales de
                  alguien que ya no existe en el sistema</>
                : 'Ninguna ficha sin dueño'}
          />
          <Linea
            alerta={sueltos.length > 0}
            texto={sueltos.length
              ? <>Registros de expediente sin colaborador:{' '}
                {sueltos.map(([nombre, filas], i) => (
                  <span key={nombre}>
                    {i > 0 && ', '}<strong>{nombre}</strong> ({(filas as unknown[]).length})
                  </span>
                ))}</>
              : 'Todo registro de expediente tiene dueño'}
          />

          {huerfanas > 0 && !limpiadas && (
            <li className="mt-2 rounded-sm border border-warn/40 bg-warn/16 p-3 text-2 text-ink-2">
              Inactivarlas las saca del maestro y deja constancia en la auditoría. No se borra
              nada: el dato sigue existiendo por si hay que consultarlo.
            </li>
          )}
        </ul>
      )}
    </Dialog>
  );
}

function Linea({ alerta, texto }: { alerta: boolean; texto: React.ReactNode }) {
  return (
    <li className="flex items-baseline gap-2">
      <i
        className={cn('bi shrink-0', alerta
          ? 'bi-exclamation-triangle-fill text-danger-ink'
          : 'bi-check2-circle text-ok-ink')}
        aria-hidden="true"
      />
      <span className="min-w-0 text-ink">{texto}</span>
    </li>
  );
}
